/**
 * typeaheadStock — instant client-side responses for the top-frequency
 * category queries. SPD v5.83 · Phase 2c (2026-07-14) — response to
 * operator feedback that a 10-second LLM latency on "shoes" was killing
 * the UX. These stock responses render in < 5 ms so the user sees chips
 * immediately; the real LLM call runs in parallel and quietly upgrades
 * the payload with a smarter intro_line + chip set when it lands.
 *
 * Rules for what goes here:
 *   · Only single-noun categories (partial length ≤ 12 chars).
 *   · Chips must be broadly generic (not brand-biased or locale-biased).
 *   · Canadian English throughout.
 *   · Keep the count small — 10-15 heads max. Anything niche should
 *     wait for the LLM.
 */

const STOCK = {
  // ── commerce_category ──────────────────────────────────────
  shoes: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['Running', 'Casual', 'Dress', 'Boots', 'Sneakers', 'Formal'],
    freetext_hint: 'e.g. for men, under $150, size 10, waterproof',
    intro_line: 'Shoes — a big category. Any particular kind, budget, or where I should be looking?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  clothes: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ["Men's", "Women's", 'Kids', 'Casual', 'Formal', 'Winter'],
    freetext_hint: 'e.g. for men, size Medium, under $100',
    intro_line: 'Clothes — a broad category. Any style, size, or budget in mind?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  clothing: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ["Men's", "Women's", 'Kids', 'Casual', 'Formal', 'Winter'],
    freetext_hint: 'e.g. for women, size Medium, under $100',
    intro_line: 'Clothing — a broad category. Any style, size, or budget in mind?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  laptop: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['MacBook', 'Windows', 'Gaming', 'Business', 'Student', 'Under $1000'],
    freetext_hint: 'e.g. 13-inch, 16GB RAM, for coding',
    intro_line: 'Laptops — a broad market. Any use case, brand, or budget in mind?',
    needs_location: false,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  laptops: null, // alias
  phone: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['iPhone', 'Samsung', 'Google', 'OnePlus', 'Refurbished', 'Under $500'],
    freetext_hint: 'e.g. 128GB, unlocked, under $700',
    intro_line: 'Phones — a big category. Any brand, storage, or budget in mind?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  phones: null,
  hotel: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['Downtown', 'Airport', 'Family', 'Business', 'Pet-friendly', 'Under $200'],
    freetext_hint: 'e.g. two nights in Toronto, near the CN Tower',
    intro_line: 'Hotels — where, when, and what kind of stay?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  hotels: null,
  car: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['SUV', 'Sedan', 'EV', 'Hybrid', 'Used', 'Under $30k'],
    freetext_hint: 'e.g. AWD, under 60,000 km, family-sized',
    intro_line: 'Cars — a big category. Any body type, powertrain, or budget in mind?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  cars: null,
  coffee: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['Beans', 'Ground', 'Espresso', 'Decaf', 'Cold brew', 'Single-origin'],
    freetext_hint: 'e.g. medium roast, fair-trade, whole bean',
    intro_line: 'Coffee — beans, machine, or a shop nearby?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  restaurants: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['Italian', 'Japanese', 'Vegan', 'Family', 'Under $30', 'Nearby'],
    freetext_hint: 'e.g. dinner for four, patio, downtown',
    intro_line: 'Restaurants — any cuisine, vibe, or budget in mind?',
    needs_location: true,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
  restaurant: null,
  vitamins: {
    query_class: 'commerce_category',
    layout_hint: 'two_col_compare',
    chips_style: 'commerce_type',
    source_strategy: 'local_bigplayer_compare',
    chips: ['Vitamin C', 'Vitamin D', 'B-Complex', 'Multivitamin', 'Kids', 'Prenatal'],
    freetext_hint: 'e.g. for immunity, chewable, adult',
    intro_line: 'Vitamins — any specific one or a general wellness goal?',
    needs_location: false,
    needs_price_range: true,
    intent_confidence: 0.9,
  },
};

// Resolve aliases (null values point to the canonical entry).
Object.entries(STOCK).forEach(([k, v]) => {
  if (v === null) {
    // Try to find canonical by trimming trailing 's'
    const canonical = k.replace(/s$/, '');
    if (STOCK[canonical]) STOCK[k] = STOCK[canonical];
  }
});

/**
 * Given a partial query, return an instant stock response object (same
 * shape as the LLM response) if we have one, else null. Match is exact
 * on the lowercase-trimmed partial — no fuzzy matching (we don't want
 * to surface stock chips for "shoes for running" which is already
 * narrowed).
 */
export function getStockResponse(partial) {
  const key = String(partial || '').trim().toLowerCase();
  if (!key) return null;
  return STOCK[key] || null;
}
